<template>
  <div class="progress-tracker p-4">
    <!-- Error State -->
    <div v-if="isError" class="text-center py-4">
      <div class="mb-3">
        <i class="fas fa-exclamation-circle fa-4x text-danger"></i>
      </div>
      <h5 class="text-danger font-weight-bold">Report Generation Failed</h5>
      <p class="text-muted">{{ progressData && progressData.currentStepDescription || 'An unexpected error occurred.' }}</p>
      <b-button variant="outline-primary" @click="$emit('generate-another')">
        <i class="fas fa-redo mr-1"></i> Try Again
      </b-button>
    </div>

    <!-- Complete State -->
    <div v-else-if="isComplete" class="text-center py-4">
      <div class="mb-3 text-success">
        <i class="fas fa-check-circle fa-4x"></i>
      </div>
      <h5 class="font-weight-bold text-success">Your Report is Ready!</h5>
      <p class="text-muted small mt-1 mb-4">
        <span v-if="progressData && progressData.versionNumber">Version {{ progressData.versionNumber }}</span>
      </p>
      <div class="d-flex justify-content-center flex-wrap" style="gap: 8px;">
        <b-button variant="primary" @click="$emit('view-report')" v-if="canViewReport">
          <i class="fas fa-file-pdf mr-1"></i> View Report
        </b-button>
        <b-button variant="outline-secondary" @click="$emit('go-to-files')">
          <i class="fas fa-folder-open mr-1"></i> Go to Files
        </b-button>
      </div>
      <div class="mt-3">
        <b-link class="small text-muted" @click="$emit('generate-another')">
          <i class="fas fa-redo mr-1"></i> Generate a New Version
        </b-link>
      </div>
    </div>

    <!-- In-Progress State -->
    <div v-else>
      <h6 class="font-weight-bold mb-3">Generating Your Report...</h6>

      <!-- Step Flow -->
      <div class="step-list mb-4">
        <div
          v-for="(step, index) in REPORT_STEPS"
          :key="step.key"
          class="step-item d-flex align-items-center py-2"
          :class="getStepClass(index)"
        >
          <div class="step-icon mr-3">
            <span v-if="isStepComplete(index)" class="text-success">
              <i class="fas fa-check-circle fa-lg"></i>
            </span>
            <span v-else-if="isStepActive(index)">
              <b-spinner small variant="primary"></b-spinner>
            </span>
            <span v-else class="text-muted">
              <i class="fas fa-circle fa-sm"></i>
            </span>
          </div>
          <div class="flex-grow-1">
            <span :class="isStepActive(index) ? 'font-weight-bold text-primary' : isStepComplete(index) ? 'text-success' : 'text-muted'">
              <i :class="`fas fa-${step.icon} mr-1`"></i>
              {{ step.label }}
            </span>
            <span v-if="isStepActive(index) && progressData && progressData.currentStepDescription"
                  class="text-muted small ml-2">
              {{ progressData.currentStepDescription }}
            </span>
          </div>
          <div v-if="isStepActive(index) && progressData && progressData.progressPercent != null"
               class="text-primary small font-weight-bold ml-2">
            {{ progressData.progressPercent }}%
          </div>
        </div>
      </div>

      <!-- Progress Bar -->
      <b-progress
        :value="progressPercent"
        :max="100"
        animated
        striped
        variant="primary"
        height="8px"
        class="mb-2"
      />
      <div class="text-center text-muted small">
        {{ progressPercent }}% complete
      </div>

      <p class="text-muted small text-center mt-3 mb-0">
        <i class="fas fa-info-circle mr-1"></i>
        You may close this window — your report will be available in the Files tab when done.
      </p>
    </div>
  </div>
</template>

<script>
const REPORT_STEPS = [
  { key: 'Queued',              label: 'Waiting to Start',          icon: 'clock',        stepValue: 0 },
  { key: 'GatheringData',       label: 'Gathering Incident Details', icon: 'database',      stepValue: 1 },
  { key: 'ValidatingAssets',    label: 'Preparing Photos & Videos', icon: 'images',        stepValue: 2 },
  { key: 'GeneratingAISummary', label: 'Writing AI Summary',        icon: 'robot',         stepValue: 3 },
  { key: 'BuildingDocument',    label: 'Building Report Layout',    icon: 'file-alt',      stepValue: 4 },
  { key: 'ConvertingToPdf',     label: 'Converting to PDF',         icon: 'file-pdf',      stepValue: 5 },
  { key: 'MergingDocuments',    label: 'Attaching Documents',       icon: 'paperclip',     stepValue: 6 },
  { key: 'Saving',              label: 'Saving Report',             icon: 'save',          stepValue: 7 },
  { key: 'Complete',            label: 'Report Ready!',             icon: 'check-circle',  stepValue: 8 }
]

export default {
  name: 'IncidentReportProgressTracker',
  props: {
    progressData: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      REPORT_STEPS
    }
  },
  computed: {
    isComplete () {
      return this.progressData && this.progressData.isComplete
    },
    isError () {
      return this.progressData && this.progressData.isError
    },
    canViewReport () {
      return this.progressData && this.progressData.generatedFileResourceId
    },
    currentStepValue () {
      if (!this.progressData) return 0
      const step = this.progressData.currentStep
      if (typeof step === 'number') return step
      // Handle string step name (case-insensitive)
      const found = REPORT_STEPS.find(s => s.key.toLowerCase() === String(step).toLowerCase())
      return found ? found.stepValue : 0
    },
    progressPercent () {
      if (this.progressData && this.progressData.progressPercent != null) {
        return this.progressData.progressPercent
      }
      // Estimate from step
      const percent = Math.round((this.currentStepValue / 8) * 100)
      return Math.min(percent, 95)
    }
  },
  methods: {
    getStepIndex (step) {
      return REPORT_STEPS.indexOf(step)
    },
    isStepComplete (index) {
      return this.currentStepValue > REPORT_STEPS[index].stepValue
    },
    isStepActive (index) {
      return this.currentStepValue === REPORT_STEPS[index].stepValue && !this.isComplete
    },
    getStepClass (index) {
      if (this.isStepComplete(index)) return 'step-done'
      if (this.isStepActive(index)) return 'step-active'
      return 'step-pending'
    }
  }
}
</script>

<style scoped>
.step-list {
  border-left: 2px solid #e9ecef;
  padding-left: 16px;
  margin-left: 8px;
}
.step-item {
  position: relative;
  transition: opacity 0.2s;
}
.step-pending {
  opacity: 0.45;
}
.step-icon {
  min-width: 24px;
  text-align: center;
}
</style>
